import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { axiosServiceInstance } from '@onesource/services';
import { Link, useLocation } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { RiExternalLinkLine, RiLockLine, RiLockUnlockLine } from 'react-icons/ri';
import { DraftBadge } from 'src/components/draft-badge';
import { match } from 'ts-pattern';

import { collectFolderIds } from './collect-folder-ids';
import type { SidebarItem, SidebarSubItem } from './types';

/**
 * A folder should open when the active route is (or lives under) one of its
 * children — i.e. the parent space of the open space/sub-space. Lets navigating
 * to a space (e.g. from an /apps card) auto-expand its folder in the tree.
 */
/**
 * Segment-aware active check: the location is `path` or a descendant of it.
 * Avoids a slug prefix-matching a sibling (e.g. `/ryc-pro-collective` vs
 * `/ryc-pro-collective-2`).
 */
const isPathActive = (pathname: string, path: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

/** A row's own route is active. Shortcuts never are — their route belongs to another menu. */
const isRowActive = (item: SidebarSubItem, pathname: string): boolean =>
  !item.isShortcut && !!item.path && isPathActive(pathname, item.path);

const containsActiveRoute = (child: SidebarSubItem, pathname: string): boolean =>
  match(child)
    .when(
      c => isRowActive(c, pathname),
      () => true,
    )
    .when(
      c => (c.children ?? []).some(grandChild => isRowActive(grandChild, pathname)),
      () => true,
    )
    .otherwise(() => false);

/**
 * Node ids joined into a single string, so the expansion effect below can depend
 * on the *value* of the active-folder set instead of an array identity. The
 * sidebar rebuilds its item tree on every query refetch (new arrays, identical
 * contents), and an identity dep would collapse the tree on each one.
 * Newlines never occur in a space id or app key, so this round-trips exactly.
 */
const joinNodeIds = (ids: string[]): string => ids.join('\n');
const splitNodeIds = (key: string): string[] => (key === '' ? [] : key.split('\n'));

interface OttGenerationResponse {
  ott: string;
  redirectUrl: string;
  expiresIn: number;
}

interface SubmenuPanelProps {
  item: SidebarItem;
  // Kept mounted while closed so the slide-out can animate; `open` drives the
  // transition instead of conditional rendering.
  open: boolean;
  // Changes whenever the panel is opened from its rail item (Community) rather
  // than by the route; each change expands every folder.
  expandAllKey?: number;
}

/**
 * The clickable row content for a tree node — a space / sub-space link, an
 * external app launcher (OTT), or a plain label. Rendered as the TreeItem `label`.
 */
const SubItemLabel = ({ subItem }: { subItem: SidebarSubItem }) => {
  const location = useLocation();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const isActive = isRowActive(subItem, location.pathname);
  const Icon = subItem.icon;

  // Program-group heading — a static, non-interactive section label.
  if (subItem.isGroupLabel) {
    return (
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'grey.600',
          py: 0.75,
        }}
        dangerouslySetInnerHTML={{ __html: subItem.label }}
      />
    );
  }

  const handleExternalAppClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!subItem.externalApp || loading) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosServiceInstance.post<OttGenerationResponse>('/auth/generate-ott/user', {
        targetApp: subItem.externalApp.appId,
        redirectUrl: subItem.externalApp.url,
        ...(subItem.externalApp.redirectTo ? { redirectTo: subItem.externalApp.redirectTo } : {}),
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.redirectUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.error('Failed to generate OTT:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {Icon && (
          <Box
            component="span"
            sx={{ display: 'flex', flexShrink: 0, color: subItem.isLocked ? '#B3B3B3' : 'grey.600' }}
          >
            <Icon size={14} />
          </Box>
        )}
        <Typography
          component="span"
          noWrap
          sx={{
            fontSize: 12,
            fontWeight: isActive ? 700 : 400,
            color: subItem.isLocked ? '#B3B3B3' : '#0F0F0F',
          }}
          dangerouslySetInnerHTML={{ __html: subItem.label }}
        />
        {subItem.isDraft && <DraftBadge sx={{ flexShrink: 0 }} />}
      </Box>
      {loading ? (
        <CircularProgress size={12} sx={{ ml: 1 }} />
      ) : subItem.externalApp ? (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <RiExternalLinkLine size={12} color={theme.palette.grey[900]} />
        </Box>
      ) : (
        subItem.isLocked !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {subItem.isLocked ? (
              <RiLockLine size={12} color={theme.palette.grey[900]} />
            ) : (
              <RiLockUnlockLine size={12} color={theme.palette.grey[900]} />
            )}
          </Box>
        )
      )}
    </Box>
  );

  // Progress-locked row (prerequisite unfinished): show why and don't navigate,
  // matching the module cards in the space body.
  if (subItem.lockMessage) {
    return (
      <Tooltip title={subItem.lockMessage} placement="right" arrow>
        <Box sx={{ cursor: 'not-allowed' }}>{content}</Box>
      </Tooltip>
    );
  }

  // External apps launch via OTT (no route); internal paths navigate via Link.
  // Folders (spaces with sub-spaces) also navigate to their space home — the
  // active-route expansion then opens the folder, matching the /apps card.
  if (subItem.externalApp) {
    return (
      <Box onClick={handleExternalAppClick} sx={{ cursor: loading ? 'default' : 'pointer' }}>
        {content}
      </Box>
    );
  }

  if (subItem.path) {
    return (
      <Link to={subItem.path} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        {content}
      </Link>
    );
  }

  return content;
};

// Width of the horizontal connector tick; doubles as the group's left padding so
// the tick spans exactly from the vertical guide line to the row.
const CONNECTOR_WIDTH = 10;

// The tick joining the group's vertical guide line to a nested row (forms an L
// with it). Only rows inside a group — depth > 0 — get one.
const connectorSx = {
  content: '""',
  position: 'absolute',
  left: -CONNECTOR_WIDTH,
  top: '50%',
  width: CONNECTOR_WIDTH,
  height: '1px',
  bgcolor: 'divider',
} as const;

/**
 * Recursively render a sidebar sub-item (and any nested sub-spaces) as a tree node.
 */
const renderTreeItem = (subItem: SidebarSubItem, depth = 0) => {
  const children = subItem.children ?? [];
  const isGroupLabel = !!subItem.isGroupLabel;

  return (
    <TreeItem
      key={subItem.id}
      nodeId={subItem.id}
      label={<SubItemLabel subItem={subItem} />}
      sx={{
        // Extra space above each group header separates one program group from
        // the next (`:first-of-type` keeps the top group flush with the panel).
        ...(isGroupLabel && { mt: 1.75, '&:first-of-type': { mt: 0 } }),
        // Indent a group's apps/spaces under their (non-collapsible) header.
        ...(depth === 0 && !isGroupLabel && { ml: 1.5 }),
        '& > .MuiTreeItem-content': {
          position: 'relative',
          py: 0,
          px: 0.5,
          borderRadius: '5px',
          // Group headers are static labels — no hover/selection affordance.
          ...(isGroupLabel
            ? { cursor: 'default', '&:hover': { bgcolor: 'transparent' } }
            : {
                '&:hover': { bgcolor: 'action.hover' },
                '&.Mui-selected, &.Mui-selected.Mui-focused, &.Mui-selected:hover': {
                  bgcolor: '#FFE9DB66',
                },
              }),
          ...(depth > 0 && { '&::before': connectorSx }),
        },
        // No expand/collapse icon is rendered (see TreeView's icon props below),
        // so the icon slot always collapses — keeps the label flush with the
        // connector tick for every row, folder or leaf.
        '& > .MuiTreeItem-content .MuiTreeItem-iconContainer': {
          width: 0,
          marginRight: 0,
        },
      }}
    >
      {children.map(child => renderTreeItem(child, depth + 1))}
    </TreeItem>
  );
};

export const SubmenuPanel = ({ item, open, expandAllKey }: SubmenuPanelProps) => {
  const location = useLocation();
  const children = useMemo(() => item.children ?? [], [item.children]);

  // Folders whose (grand)child is the active route — the space you're currently on.
  const activeParentKey = useMemo(
    () => joinNodeIds(children.filter(child => containsActiveRoute(child, location.pathname)).map(child => child.id)),
    [children, location.pathname],
  );

  // Expansion follows the active route: only the folder for the space you're on
  // (its home or a sub-space page) is open — so a folder is collapsed everywhere
  // else, and stays collapsed on /apps or any non-program page even after a
  // refresh. Re-derived on every navigation (not accumulated) — including one
  // within the same folder, so opening a community after "expand all" collapses
  // the rest — while still allowing a manual toggle until the route changes.
  const [expanded, setExpanded] = useState<string[]>(() => splitNodeIds(activeParentKey));
  useEffect(() => {
    setExpanded(splitNodeIds(activeParentKey));
  }, [activeParentKey, location.pathname]);

  // Opened from the rail item instead (Community): every folder starts expanded.
  // Declared after the route effect so it wins when both run on mount.
  useEffect(() => {
    if (expandAllKey !== undefined) {
      setExpanded(collectFolderIds(children));
    }
    // `children` is left out on purpose: a refetch rebuilding the tree must not
    // re-expand folders the user has since collapsed.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllKey]);

  if (children.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        ml: 0.3,
        position: 'absolute',
        left: 70,
        top: 0,
        width: 245,
        minHeight: '100%',
        bgcolor: '#fafafa',
        borderRadius: '0 20px 0 0',
        boxShadow: '14px 17px 40px 0px rgba(112,144,176,0.08)',
        py: '20px',
        px: 1.5,
        // Below the icon rail (which paints its own background above), so the
        // panel slides out from underneath it rather than over it.
        zIndex: 1,
        // Slide + fade in step with SubNavPageContainer's 0.2s content offset.
        // `visibility` waits for the transition, keeping the hidden panel out of
        // the tab order without cutting the slide-out short.
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        opacity: open ? 1 : 0,
        visibility: open ? 'visible' : 'hidden',
        pointerEvents: open ? 'auto' : 'none',
        transition: 'transform 0.2s ease-in-out, opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
      }}
    >
      {!item.hideTitle && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: '#0F0F0F',
              bgcolor: '#FFEEE8',
              px: 0.8,
              py: 0.25,
              borderRadius: '5px',
            }}
          >
            {item.label}
          </Typography>
        </Box>
      )}
      <TreeView
        aria-label={`${item.label} navigation`}
        expanded={expanded}
        onNodeToggle={(_event, nodeIds) => setExpanded(nodeIds)}
        // No expand/collapse/end icon — rows are distinguished by indentation
        // and the connector guide lines only.
        defaultCollapseIcon={null}
        defaultExpandIcon={null}
        defaultEndIcon={null}
        sx={{
          '& .MuiTreeItem-group': {
            ml: 1.5,
            pl: `${CONNECTOR_WIDTH}px`,
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {children.map(subItem => renderTreeItem(subItem))}
      </TreeView>
    </Box>
  );
};
